/**
 * Day-scope wrapper for experimentComparison (Phase 16).
 *
 * Detects A/B experiments that have at least one trade today, then runs
 * the experimentComparison analyzer on the FULL cross-day group of trades
 * sharing each experiment label. Why cross-day: an experiment started
 * Tuesday and continued today should be evaluated as one ongoing test,
 * not sliced per calendar day.
 *
 * Returns an array of observations — one per experiment that the inner
 * analyzer didn't silently null out (need ≥2 variants, etc.). When no
 * trades today carry an `experiment` value, returns null and stays quiet.
 */

import * as experimentComparison from '../composite/experimentComparison.js';

export const key = 'dayExperiments';
export const tier = 'free';

export const analyze = (ctx) => {
  const { day, allTrades = [], getTradeNetPnL, getAccountById } = ctx || {};
  if (!day || !Array.isArray(day.trades) || day.trades.length === 0) return null;

  // Find experiment names that have ≥1 trade today. Trades without an
  // experiment value contribute nothing — Coach doesn't nag traders who
  // don't run experiments.
  const todaysExperiments = new Set();
  for (const t of day.trades) {
    const name = (t?.experiment || '').trim();
    if (name) todaysExperiments.add(name);
  }
  if (todaysExperiments.size === 0) return null;

  // day.trades is already netPnl-enriched (buildDayContext does this).
  // For trades from prior days that share the same experiment, we need to
  // do the same enrichment so the analyzer's win/loss/avgR math works.
  const dayTradesById = new Map(day.trades.map(t => [t.id, t]));
  const enrichOne = (t) => {
    if (Number.isFinite(t.netPnl)) return t;  // already enriched
    if (typeof getTradeNetPnL === 'function') {
      const acc = typeof getAccountById === 'function' ? getAccountById(t.accountId) : null;
      const r = getTradeNetPnL(t, acc);
      return { ...t, netPnl: Number.isFinite(r?.netPnl) ? r.netPnl : (parseFloat(t.pnl) || 0) };
    }
    return { ...t, netPnl: parseFloat(t.pnl) || 0 };
  };

  const observations = [];
  for (const expName of todaysExperiments) {
    // Gather every trade in the user's history matching this experiment label
    const expTrades = (allTrades || [])
      .filter(t => (t?.experiment || '').trim() === expName)
      .map(t => dayTradesById.get(t.id) || enrichOne(t));

    const result = experimentComparison.analyze({
      experimentName: expName,
      experimentTrades: expTrades,
    });
    if (result) observations.push(result);
  }

  return observations.length > 0 ? observations : null;
};
